import { type AxiosInstance } from 'axios'

type Labels = {
  label_name: string
  label_description: string
  priority: string
}

type CompanyLocation = {
  building_name: string
  floor: number
  unit_number: number
  facility_type: string
  street: string
  state: string
  country: string
  pincode: number
  location_nickname: string
  company: number
}

interface Company {
  name: string
  address: string
  country_of_operation: string
  industry?: number | null
  website?: string
  classification?: number | null
  revenue?: number | null
  currency?: number | null
  reporting_end?: number
}

type TokenData = {
  signal: AbortSignal | undefined
}

interface labelData {
  label_name: string
  label_description: string
  priority?: 'high' | 'medium' | 'low' | 'unassigned'
}

interface assetLabel {
  asset_id: number
  company_id: number
  category_id: number
  labels?: string
}

interface UserEmail {
  email: string
}

type ApprovalData = {
  approval_ids: number[]
  status: string
  rejection_message?: string
  tagged_users?: number[]
}

type logData = {
  remark: string | null
  date: string | null | Date
  category_id: number | null
  item_name: string | null
  unit_id: number | null
  labels?: Array<number>
  value: number | null
  asset_id: number | null
  emission_factor_uid: number | null
  calculation_method: string | null
  supporting_documents: Array<string> | []
}

interface AnswerData {
  type_id: number
  source_id: number
  date: string
  category_id: string
  labels?: number[]
  from_bulk_upload?: boolean
  file_name?: string
  emission_data_logs: {
    option: number
    data_values: {
      question_id: number
      data: number
      unit_id: number
    }[]
  }[]
}

type UpdateCompanyDataRequest = {
  website: string
  revenue: number | null
  currency: number // TODO: Set proper typings
  reporting_end: string // TODO: Set proper typings
}

type GroupedApprovalSubmissionsRequestParams = {
  // Assuming all params are optional
  page?: number
  page_size?: number
  status?: string // can also be type
  sort_by_created_on?: string
  sort_by_updated_at?: string
  submitter?: number | null
  category?: string
  asset?: number | null
  updater?: string
  created_on_start_date?: string
  created_on_end_date?: string
  last_updated_at_start_date?: string
  last_updated_at_end_date?: string
}

type commentPayload = {
  company: number | string
  comment_id: number | string
  comment: string
  recipients?: string
  data_owner_link?: string
  approver_link?: string
  submission?: number | string
}

type CompanyUser = {
  id: number
  name: string
}

interface GetAllUsersParams extends PaginationParams {
  name_query?: string
}

export default (axios: AxiosInstance) => ({
  fetchAll() {
    return axios.get('/v1/companies/')
  },
  save(company: Company) {
    return axios.post('/v1/companies/', company)
  },
  fetchCompany(companyId: number) {
    return axios.get(`/v1/companies/${companyId}/`)
  },
  listCompanyLabels(companyId: number, params: PaginationParams = { page: 1, page_size: 15 }) {
    return axios.get<PaginatedResponse<CompanyLabel>>(`/v1/companies/company/${companyId}/labels/`, { params })
  },
  queryLabelsByName(companyId: number, query: string, token: TokenData) {
    return axios.get(`/v1/companies/company/${companyId}/labels-list/?search=${query}&page_size=100`, {
      signal: token.signal,
    })
  },
  listUserFiles(limit?: number) {
    const query = limit === undefined ? '' : `?limit=${limit}`
    return axios.get(`/v1/companies/files/user-files/${query}`)
  },
  deleteUserFile(id: string) {
    return axios.delete(`/v1/companies/files/user-files/${id}/`)
  },
  emissionLogsPresent(companyId: number) {
    return axios.get(`/v1/companies/company/${companyId}/emission-logs-present/`)
  },
  getApprovalItemNames() {
    return axios.get(`/v1/companies/company/approval-item-names/?show_submission_items=false`)
  },
  groupedApprovalSubmissions(params: GroupedApprovalSubmissionsRequestParams) {
    return axios.get(`/v1/companies/emissions/grouped-approval-submissions/`, { params: params })
  },
  labelCreate(companyId: number, createData: labelData) {
    return axios.post(`/v1/companies/company/${companyId}/labels/create/`, createData)
  },

  labelUpdate(companyId: number, labeId: number | string, updateData: labelData) {
    return axios.put(`/v1/companies/company/${companyId}/labels/label/${labeId}/`, updateData)
  },
  getLabel(companyId: number, labeId: number | string) {
    return axios.get(`/v1/companies/company/${companyId}/labels/label/${labeId}/`)
  },

  uploadEmissionData(companyId: number, answers: AnswerData) {
    return axios.post(`/v1/companies/company/${companyId}/upload-emission-data/`, answers)
  },

  emissionItemNames(companyId: number, parameters: string = '') {
    return axios.get(`/v1/companies/company/${companyId}/item-name-emission-logs/?${parameters}`)
  },
  emissionLogs(companyId: number, paginationParams = { page: 1, page_size: 15, sort_by_date: 'asc' }, searchParam: string) {
    const { page, page_size, sort_by_date } = paginationParams
    let queryString = '?'
    if (!isNaN(page) && !isNaN(page_size)) {
      queryString += `page=${page}&page_size=${page_size}`
    }
    if (!isNaN(page) && isNaN(page_size)) {
      queryString += `page=${page}`
    }
    if (isNaN(page) && !isNaN(page_size)) {
      queryString += `page_size=${page_size}`
    }
    if (searchParam) {
      queryString += searchParam
    }
    if (sort_by_date) {
      queryString += `&sort_by_date=${sort_by_date}`
    }
    return axios.get(`/v1/companies/company/${companyId}/emissions/logs/${queryString}`)
  },
  getEmissionAuditLogs(companyId: number, params: GetEmissionAuditLogsQueryParams) {
    return axios.get(`/v1/companies/company/${companyId}/emissions/logs`, { params })
  },
  updateEmissionLogData(companyId: number, postData: logData, logID: number | string, factorStatus: boolean = false) {
    return axios.put(`/v1/companies/company/${companyId}/${logID}/update-emission-data/?require_factor=${factorStatus}`, postData)
  },
  uploadDraftEmissionData(companyId: number | string, postData: logData, queryString: string = '', isSingle: boolean = false) {
    return axios.post(`/v1/companies/company/${companyId}/upload-draft-emission-data/?single=${isSingle}${queryString}`, postData)
  },
  uploadDraftEmissionDataNew(companyId: number, postData: EsgDraftEntryData | EsgDraftEntryData[], params: UploadDraftEmissionParams) {
    return axios.post(`/v1/companies/company/${companyId}/upload-draft-emission-data/`, postData, { params })
  },
  bulkUploadDraftEmissionData(companyId: number, postData: logData, bulkQueryParams: string, isBulk: boolean = false) {
    return axios.post(`/v1/companies/company/${companyId}/upload-draft-emission-data/${isBulk ? `?${bulkQueryParams}` : ''}`, postData)
  },
  getEmissionLogData(logId: number | string) {
    return axios.get(`/v1/companies/company/emission-logs/${logId}/`)
  },
  deleteLogData(sources: number[]) {
    return axios.put(`/v1/companies/company/emission-logs/multiple/delete/`, { ids: sources })
  },
  approvalItemsName() {
    return axios.get(`/v1/companies/company/approval-item-names/?show_submission_items=true`)
  },
  emissionSubmissions(queryString: string) {
    return axios.get(`/v1/companies/emissions/submissions/${queryString}`)
  },
  approvalSubmission(selectedLogId: number | string) {
    return axios.get(`/v1/companies/emissions/approvals/${selectedLogId}/approvals-by-submission/`)
  },
  deleteDraftEmissionData(categoryId: number | string) {
    return axios.delete(`/v1/companies/delete-draft-emissiondata/category/${categoryId}/`)
  },
  emissionLogData(companyId: number, queryString: string = '') {
    return axios.get(`/v1/companies/company/${companyId}/emissions/logs/${queryString}`)
  },
  // similar to emissionLogData & emissionLogs above for backwards compaibility & typing
  retrieveEmissionLogs(companyId: number, params?: EmissionLogsQueryParams) {
    return axios.get<PaginatedResponse<EmissionLog>>(`/v1/companies/company/${companyId}/emissions/logs/`, { params })
  },
  resetSubmission(companyId: number, submissionId: string) {
    return axios.post(`/v1/companies/company/${companyId}/reset-submission/${submissionId}/`)
  },
  applyAssetToEmission(companyId: number, categoryId: number | string, assetId: number, queryString: string = '') {
    return axios.put(`/v1/companies/company/${companyId}/category/${categoryId}/emissions-apply-asset-and-labels-to-all/?asset_id=${Number(assetId)}${queryString}`)
  },
  applyLabelToEmission(companyId: number, categoryId: number | string, labels: assetLabel, queryString: string = '') {
    return axios.put(`/v1/companies/company/${companyId}/category/${categoryId}/emissions-apply-asset-and-labels-to-all/?labels=${labels}${queryString}`)
  },
  deleteMultipleLogs(sources: number[]) {
    return axios.put(`/v1/companies/company/emission-logs/multiple/delete/`, { ids: sources })
  },
  updateMultipleApprovals(approvalIds: ApprovalData) {
    return axios.put(`/v1/companies/emissions/approvals/update_multiple_approvals/`, approvalIds)
  },
  updateCompany(id: number, data: UpdateCompanyDataRequest) {
    return axios.patch(`/v1/companies/${id}/`, data)
  },
  createLabels(companyId: number, data: Labels) {
    return axios.post(`/v1/companies/company/${companyId}/labels/create/`, data)
  },
  getLabels(companyId: number, id: number) {
    return axios.get(`/v1/companies/company/${companyId}/labels/label/${id}/`)
  },
  updateLabels(companyId: number, id: number, data: Labels) {
    return axios.put(`/v1/companies/company/${companyId}/labels/label/${id}/`, data)
  },
  getCompanyLocations(companyId: number) {
    return axios.get(`/v1/companies/company/locations/${companyId}/`)
  },
  createCompanyLocations(data: CompanyLocation) {
    return axios.post(`/v1/companies/company/locations/`, data)
  },
  updateCompanyLocations(id: number) {
    return axios.patch(`/v1/companies/company/locations/location/${id}/`)
  },
  getCompanyLocationsById(id: number) {
    return axios.get(`/v1/companies/company/locations/location/${id}/`)
  },
  updateCompanyLocationsById(id: number, data: CompanyLocation) {
    return axios.put(`/v1/companies/company/locations/location/${id}/`, data)
  },
  fetchLabels(companyId: number) {
    return axios.get(`/v1/companies/company/${companyId}/labels/`)
  },
  listMembers(companyId: number) {
    return axios.get(`/v1/companies/company/${companyId}/members/`)
  },
  ownersApproversList(companyId: number) {
    return axios.get(`/v1/companies/company/${companyId}/owners-approvers-list/`)
  },

  getRegister() {
    return axios.get(`/v1/companies/administrators/allowed-to-register/`)
  },

  allowedToRegister(data: UserEmail) {
    return axios.post(`/v1/companies/administrators/allowed-to-register/`, data)
  },
  exportEmissionLogs(companyId: number, params?: EmissionLogFilterParams) {
    // Call the export with a timeout of 100 seconds
    return axios.get(
      `/v1/companies/company/${companyId}/emission-logs/export-data/`,
      { responseType: 'blob', timeout: 100000, params },
    )
  },
  comments(companyId: number, submissionId: number | string) {
    return axios.get(`/v1/comments/company/${companyId}/submission/${submissionId}/rejection-messages/`)
  },
  saveReplyComment(params: commentPayload) {
    const { company, comment_id, comment, recipients, data_owner_link, approver_link, submission } = params
    return axios.post(
      `/v1/comments/company/${company}/submission/${submission}/parent-comment/${comment_id}/reply/`,
      { comment },
      {
        params: {
          recipients: recipients || '',
          data_owner_link: data_owner_link || '',
          approver_link: approver_link || '',
        },
      },
    )
  },
  editComment(params: commentPayload) {
    const { company, comment_id, comment } = params
    return axios.put(`/v1/comments/company/${company}/comment/${comment_id}/update-rejected-message/`, { comment })
  },
  resolveComment(companyId: number, commentId: number | string, resolveStatus: boolean) {
    return axios.put(`/v1/comments/company/${companyId}/comment/${commentId}/update-rejected-message/`, { resolved: resolveStatus })
  },
  commentApprovers(companyId: number, submissionId: number | string) {
    return axios.get(`/v1/comments/company/${companyId}/submission/${submissionId}/approvers-data-owners-list/`)
  },
  getAllUsers(params: GetAllUsersParams) {
    return axios.get<PaginatedResponse<CompanyUser | AutoCompleteItem>>('/v1/get-all-users/', {
      params: {
        page: params.page,
        page_size: params.page_size,
        name: params.name_query,
      },
    })
  },
})
